import React, { useState, useEffect, useMemo, startTransition } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    IconButton,
    TextField,
    Button,
    InputAdornment,
    Pagination,
    Chip,
    CircularProgress,
    Avatar,
    Menu,
    MenuItem,
} from '@mui/material';
import {
    Search as SearchIcon,
    FilterList as FilterListIcon,
    GridView as GridViewIcon,
    ViewList as ViewListIcon,
    MoreVert as MoreVertIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    ArrowForward as ArrowForwardIcon,
    CalendarToday as CalendarTodayIcon,
    LocationOn as LocationOnIcon,
    Devices as DevicesIcon,
    CheckCircleOutline as CheckCircleOutlineIcon,
    PlayCircleOutline as PlayCircleOutlineIcon,
    Description as DescriptionIcon,
    GraphicEq as GraphicEqIcon,
    PhotoCamera as PhotoCameraIcon,
    Add as AddIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@mui/icons-material';

import { DeploymentAdminListDetail, DeploymentStatus } from '../../../api/Api';
import clientService from '../../../clientService';
import { useAppState } from 'src/state-management/state-provider';
import { useSnackbar } from 'src/view/components/SnackbarService';
import { formatDate } from 'src/util/date-utils';
import { useNavigate } from 'react-router-dom';
import ConfirmationDialog from 'src/view/components/ConfirmationDialog';
import DeploymentDialog from '../components/DeploymentDialog';

// Mock data for images if not available
const MOCK_IMAGES = [
    'https://images.unsplash.com/photo-1542273917363-3b1817f69a2d?auto=format&fit=crop&q=80&w=600',
    'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?auto=format&fit=crop&q=80&w=600',
    'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?auto=format&fit=crop&q=80&w=600',
    'https://images.unsplash.com/photo-1447752875215-b2761acb3c5d?auto=format&fit=crop&q=80&w=600',
];

const getStatusStyle = (status: DeploymentStatus | null | undefined) => {
    switch (status) {
        case DeploymentStatus.Upcoming:
            return { color: '#EAB308', bgcolor: 'rgba(234, 179, 8, 0.1)', label: 'UPCOMING' };
        case DeploymentStatus.Past:
            return { color: '#8B5CF6', bgcolor: 'rgba(139, 92, 246, 0.1)', label: 'PAST' };
        case DeploymentStatus.Ongoing:
            return { color: '#10B981', bgcolor: 'rgba(16, 185, 129, 0.1)', label: 'DEPLOYED' };
        default:
            return { color: '#999', bgcolor: 'rgba(153, 153, 153, 0.1)', label: 'UNKNOWN' };
    }
};

const StatCard = ({ icon: Icon, label, value, color }: { icon: React.ElementType, label: string, value: string | number, color: string }) => (
    <Card sx={{
        bgcolor: '#111',
        border: '1px solid #222',
        borderRadius: '16px',
        height: '100%',
        transition: 'transform 0.2s',
        '&:hover': { transform: 'translateY(-4px)', borderColor: '#333' }
    }}>
        <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <Box sx={{
                    p: 1,
                    borderRadius: '8px',
                    bgcolor: `${color}15`,
                    color: color,
                    display: 'flex'
                }}>
                    <Icon sx={{ fontSize: 20 }} />
                </Box>
                <Typography variant="body2" sx={{ color: '#999', fontWeight: 500 }}>
                    {label}
                </Typography>
            </Box>
            <Typography variant="h3" sx={{ color: '#FFF', fontWeight: 700 }}>
                {value}
            </Typography>
        </CardContent>
    </Card>
);

const DeploymentCard = ({ deployment, onEdit, onDelete, onClick }: { deployment: DeploymentAdminListDetail, onEdit: (d: DeploymentAdminListDetail) => void, onDelete: (d: DeploymentAdminListDetail) => void, onClick: (id: string) => void }) => {
    const status = getStatusStyle(deployment.status);
    
    // Stable image selection based on deployment ID
    const imgIndex = useMemo(() => {
        let hash = 0;
        const str = deployment.deployment_id || '';
        for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
        }
        return Math.abs(hash) % MOCK_IMAGES.length;
    }, [deployment.deployment_id]);
    
    const mockedImg = MOCK_IMAGES[imgIndex];

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    return (
        <Card sx={{
            bgcolor: '#111',
            border: '1px solid #222',
            borderRadius: '16px',
            overflow: 'hidden',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
                borderColor: '#10B981',
                '& .overlay-btn': { opacity: 1 }
            },
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
        }}>
            <Box sx={{ position: 'relative', height: 180 }}>
                <Box
                    component="img"
                    src={mockedImg}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <Box sx={{
                    position: 'absolute',
                    top: 12,
                    left: 12,
                    display: 'flex',
                    gap: 1
                }}>
                    <Chip
                        label={status.label}
                        size="small"
                        sx={{
                            bgcolor: status.bgcolor,
                            color: status.color,
                            fontWeight: 700,
                            fontSize: 10,
                            borderRadius: '4px'
                        }}
                    />
                </Box>
                <IconButton
                    size="small"
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        bgcolor: 'rgba(0,0,0,0.5)',
                        color: '#FFF',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                    }}
                >
                    <MoreVertIcon fontSize="small" />
                </IconButton>
                <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={() => setAnchorEl(null)}
                    PaperProps={{
                        sx: { bgcolor: '#1A1A1A', color: '#FFF', border: '1px solid #333' }
                    }}
                >
                    <MenuItem onClick={() => { onEdit(deployment); setAnchorEl(null); }}>
                        <EditIcon sx={{ fontSize: 18, mr: 1.5, color: '#10B981' }} /> Edit
                    </MenuItem>
                    <MenuItem onClick={() => { onDelete(deployment); setAnchorEl(null); }}>
                        <DeleteIcon sx={{ fontSize: 18, mr: 1.5, color: '#EF4444' }} /> Delete
                    </MenuItem>
                </Menu>
            </Box>
            <CardContent sx={{ p: 2.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" sx={{ color: '#FFF', fontWeight: 700, mb: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {deployment.deployment_name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2, color: '#666' }}>
                    <LocationOnIcon sx={{ fontSize: 14 }} />
                    <Typography variant="caption" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {deployment.site_name}, {deployment.location_name}
                    </Typography>
                </Box>

                <Box sx={{ flexGrow: 1 }}>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: '#555', display: 'block', mb: 0.5, textTransform: 'uppercase', fontWeight: 600 }}>Start Date</Typography>
                            <Typography variant="body2" sx={{ color: '#DDD', fontWeight: 500 }}>{formatDate(deployment.start_date)}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: '#555', display: 'block', mb: 0.5, textTransform: 'uppercase', fontWeight: 600 }}>End Date</Typography>
                            <Typography variant="body2" sx={{ color: '#DDD', fontWeight: 500 }}>{formatDate(deployment.end_date)}</Typography>
                        </Grid>
                    </Grid>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                    <Box sx={{ display: 'flex', gap: 3 }}>
                        <Box>
                            <Typography sx={{ color: '#666', fontSize: 10, fontWeight: 700, mb: 0.2 }}>ANALYSED</Typography>
                            <Typography variant="body2" sx={{ color: '#FFF', fontWeight: 700 }}>{deployment.inference_count}</Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ color: '#666', fontSize: 10, fontWeight: 700, mb: 0.2 }}>TOTAL</Typography>
                            <Typography variant="body2" sx={{ color: '#FFF', fontWeight: 700 }}>{deployment.file_count}</Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={() => onClick(deployment.deployment_id)}
                        sx={{
                            bgcolor: 'rgba(16, 185, 129, 0.1)',
                            color: '#10B981',
                            '&:hover': { bgcolor: '#10B981', color: '#FFF' }
                        }}
                    >
                        <ArrowForwardIcon fontSize="small" />
                    </IconButton>
                </Box>
            </CardContent>
        </Card>
    );
};

const DeploymentRow = ({ deployment, onEdit, onDelete, onClick }: { deployment: DeploymentAdminListDetail, onEdit: (d: DeploymentAdminListDetail) => void, onDelete: (d: DeploymentAdminListDetail) => void, onClick: (id: string) => void }) => {
    const status = getStatusStyle(deployment.status);
    return (
        <Box
            onClick={() => onClick(deployment.deployment_id)}
            sx={{
                display: 'grid',
                gridTemplateColumns: '2fr 1.5fr 1.2fr 1.2fr 1fr 1fr 120px 100px 100px',
                alignItems: 'center',
                py: 2,
                px: 2,
                borderBottom: '1px solid #111',
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.01)' },
            }}
        >
            <Typography variant="body2" sx={{ color: '#FFF', fontWeight: 500, pr: 2, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {deployment.deployment_name}
            </Typography>
            <Typography variant="body2" sx={{ color: '#999', fontSize: 13 }}>
                {deployment.project_name}
            </Typography>
            <Typography variant="body2" sx={{ color: '#999', fontSize: 13 }}>
                {deployment.site_name}
            </Typography>
            <Typography variant="body2" sx={{ color: '#999', fontSize: 13 }}>
                {deployment.device_name || '-'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#999', fontSize: 13 }}>
                {formatDate(deployment.start_date)}
            </Typography>
            <Typography variant="body2" sx={{ color: '#999', fontSize: 13 }}>
                {formatDate(deployment.end_date)}
            </Typography>
            <Box>
                <Chip
                    label={status.label}
                    size="small"
                    sx={{
                        bgcolor: status.bgcolor,
                        color: status.color,
                        fontWeight: 700,
                        fontSize: 10,
                        borderRadius: '4px',
                        height: 20
                    }}
                />
            </Box>
            <Typography variant="body2" sx={{ color: '#FFF', fontWeight: 700, textAlign: 'center' }}>
                {deployment.file_count}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(deployment); }} sx={{ color: '#10B981' }}>
                    <EditIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(deployment); }} sx={{ color: '#EF4444' }}>
                    <DeleteIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Box>
        </Box>
    );
};

const ActivityItem = ({ activity }: { activity: { icon: React.ElementType, color: string, isLast: boolean, title: string, location: string, time: string } }) => (
    <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Box sx={{ position: 'relative' }}>
            <Avatar sx={{ bgcolor: activity.color + '15', color: activity.color, width: 32, height: 32 }}>
                <activity.icon sx={{ fontSize: 16 }} />
            </Avatar>
            <Box sx={{
                position: 'absolute',
                bottom: -20,
                left: '50%',
                width: '1px',
                height: '24px',
                bgcolor: '#222',
                display: activity.isLast ? 'none' : 'block'
            }} />
        </Box>
        <Box sx={{ flexGrow: 1 }}>
            <Typography variant="body2" sx={{ color: '#FFF', fontWeight: 600 }}>
                {activity.title}
            </Typography>
            <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                at {activity.location}
            </Typography>
            <Typography variant="caption" sx={{ color: '#444' }}>
                {activity.time}
            </Typography>
        </Box>
    </Box>
);

const Deployments: React.FC = () => {
    const { state, dispatch } = useAppState();
    const { deployments } = state.admin;
    const { showSnackbar, snackbarType } = useSnackbar();
    const navigate = useNavigate();

    const [page, setPage] = useState(1);
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [rowsPerPage] = useState(9);
    const [quickFilter, setQuickFilter] = useState<DeploymentStatus | 'All'>('All');
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectedDeployment, setSelectedDeployment] = useState<DeploymentAdminListDetail | null>(null);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

    const [statusAnchorEl, setStatusAnchorEl] = useState<null | HTMLElement>(null);

    useEffect(() => {
        const fetchDeployments = async () => {
            setLoading(true);
            try {
                const response = await clientService.api.getAllDeploymentsApiV1AdminDeploymentsGet();
                startTransition(() => {
                    dispatch({ type: 'ADMIN_SET_DEPLOYMENT_LIST', payload: Array.isArray(response.data) ? (response.data as any) : ((response.data as any)?.deployments || []) });
                });
            } catch (error) {
                console.error('Failed to fetch deployments:', error);
                showSnackbar({ message: 'Failed to fetch deployments', type: snackbarType.ERROR });
            } finally {
                setLoading(false);
            }
        };

        if (deployments.length === 0) {
            fetchDeployments();
        }
    }, [deployments.length, dispatch, showSnackbar, snackbarType]);

    const filteredDeployments = useMemo(() => {
        let result = deployments;
        if (quickFilter !== 'All') {
            result = result.filter(d => d.status === quickFilter);
        }
        if (searchTerm) {
            const lowerSearch = searchTerm.toLowerCase();
            result = result.filter(d =>
                d.deployment_name.toLowerCase().includes(lowerSearch) ||
                d.project_name.toLowerCase().includes(lowerSearch) ||
                d.site_name.toLowerCase().includes(lowerSearch)
            );
        }
        return result;
    }, [deployments, quickFilter, searchTerm]);

    const paginatedDeployments = useMemo(() => {
        return filteredDeployments.slice((page - 1) * rowsPerPage, page * rowsPerPage);
    }, [filteredDeployments, page, rowsPerPage]);

    const ongoingCount = deployments.filter(d => d.status === DeploymentStatus.Ongoing).length;
    const completedCount = deployments.filter(d => d.status === DeploymentStatus.Past).length;
    const totalFiles = deployments.reduce((acc, curr) => acc + (curr.file_count || 0), 0);
    const totalInferences = deployments.reduce((acc, curr) => acc + (curr.inference_count || 0), 0);

    const recentActivities = useMemo(() => {
        return [...deployments]
            .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
            .slice(0, 5)
            .map(d => {
                let title = '';
                let icon = DevicesIcon;
                let color = '#10B981';

                switch (d.status) {
                    case DeploymentStatus.Ongoing:
                        title = `Device ${d.deployment_name} deployed`;
                        icon = DevicesIcon;
                        color = '#10B981';
                        break;
                    case DeploymentStatus.Past:
                        title = `Deployment ${d.deployment_name} completed`;
                        icon = CheckCircleOutlineIcon;
                        color = '#8B5CF6';
                        break;
                    case DeploymentStatus.Upcoming:
                        title = `Deployment ${d.deployment_name} scheduled`;
                        icon = CalendarTodayIcon;
                        color = '#EAB308';
                        break;
                    default:
                        title = `Deployment ${d.deployment_name} updated`;
                        icon = DescriptionIcon;
                        color = '#6B7280';
                }

                if (d.data_upload_status) {
                    title = `Acoustic data uploaded for ${d.deployment_name}`;
                    icon = GraphicEqIcon;
                    color = '#3B82F6';
                }

                return {
                    title,
                    location: `${d.site_name}, ${d.location_name}`,
                    time: formatDate(d.updated_at),
                    icon,
                    color
                };
            });
    }, [deployments]);

    const handleDeleteDeployment = async () => {
        if (selectedDeployment) {
            try {
                await clientService.api.deleteApiDeploymentApiV1AdminDeploymentsDeploymentIdDelete(selectedDeployment.deployment_id);
                dispatch({
                    type: 'ADMIN_SET_DEPLOYMENT_LIST',
                    payload: deployments.filter(dep => dep.deployment_id !== selectedDeployment.deployment_id)
                });
                showSnackbar({ message: 'Deployment deleted successfully' });
            } catch (error) {
                console.error('Failed to delete deployment', error);
                showSnackbar({ message: 'Failed to delete deployment', type: snackbarType.ERROR });
            }
        }
        setOpenConfirmDialog(false);
    };

    return (
        <Box sx={{ p: 4, minHeight: '100vh', bgcolor: '#000' }}>
            <Box sx={{ mb: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h4" sx={{ color: '#FFF', fontWeight: 800, mb: 1 }}>
                        Deployments
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                        Manage and monitor all field deployments across project sites.
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setIsCreateDialogOpen(true)}
                        sx={{
                            bgcolor: '#10B981',
                            color: '#FFF',
                            fontWeight: 700,
                            fontSize: '15px',
                            px: 4,
                            py: 1.5,
                            borderRadius: '14px',
                            textTransform: 'none',
                            boxShadow: '0 4px 14px 0 rgba(16, 185, 129, 0.39)',
                            '&:hover': {
                                bgcolor: '#059669',
                                boxShadow: '0 6px 20px rgba(5, 150, 105, 0.23)',
                            }
                        }}
                    >
                        New Deployment
                    </Button>
                </Box>
            </Box>

            <Grid container spacing={4}>
                <Grid item xs={12} lg={9}>
                    <Grid container spacing={3} sx={{ mb: 6 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard icon={PlayCircleOutlineIcon} label="Ongoing Deployments" value={ongoingCount} color="#10B981" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard icon={CheckCircleOutlineIcon} label="Completed Deployments" value={completedCount} color="#3B82F6" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard icon={DescriptionIcon} label="Total Files Analysed" value={totalInferences > 1000 ? `${(totalInferences / 1000).toFixed(1)}K` : totalInferences} color="#8B5CF6" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard icon={PhotoCameraIcon} label="Total Images Captured" value={totalFiles > 1000 ? `${(totalFiles / 1000).toFixed(1)}K` : totalFiles} color="#EAB308" />
                        </Grid>
                    </Grid>

                    <Box sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                        mb: 4,
                        p: 1.5,
                        bgcolor: '#111',
                        border: '1px solid #222',
                        borderRadius: '12px'
                    }}>
                        <Box sx={{ display: 'flex', gap: 2, flexGrow: 1 }}>
                            <TextField
                                size="small"
                                placeholder="Search deployments..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: '#555', fontSize: 20 }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    minWidth: 300,
                                    '& .MuiOutlinedInput-root': {
                                        bgcolor: '#1A1A1A',
                                        color: '#FFF',
                                        '& fieldset': { border: 'none' },
                                        borderRadius: '8px'
                                    }
                                }}
                            />
                            <Button
                                variant="outlined"
                                size="small"
                                endIcon={<KeyboardArrowDownIcon />}
                                onClick={(e) => setStatusAnchorEl(e.currentTarget)}
                                sx={{ bgcolor: '#1A1A1A', borderColor: '#333', color: '#FFF', textTransform: 'none', borderRadius: '8px', minWidth: 150 }}
                            >
                                {quickFilter === 'All' ? 'All Status' : quickFilter}
                            </Button>
                            <Menu
                                anchorEl={statusAnchorEl}
                                open={Boolean(statusAnchorEl)}
                                onClose={() => setStatusAnchorEl(null)}
                                PaperProps={{ sx: { bgcolor: '#1A1A1A', color: '#FFF', border: '1px solid #333', minWidth: 150 } }}
                            >
                                <MenuItem onClick={() => { setQuickFilter('All'); setStatusAnchorEl(null); }}>All Status</MenuItem>
                                <MenuItem onClick={() => { setQuickFilter(DeploymentStatus.Ongoing); setStatusAnchorEl(null); }}>Deployed</MenuItem>
                                <MenuItem onClick={() => { setQuickFilter(DeploymentStatus.Past); setStatusAnchorEl(null); }}>Past</MenuItem>
                                <MenuItem onClick={() => { setQuickFilter(DeploymentStatus.Upcoming); setStatusAnchorEl(null); }}>Upcoming</MenuItem>
                            </Menu>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button startIcon={<FilterListIcon />} sx={{ color: '#999', textTransform: 'none' }}>Filters</Button>
                            <Box sx={{ display: 'flex', bgcolor: '#1A1A1A', p: 0.5, borderRadius: '8px', border: '1px solid #333' }}>
                                <IconButton
                                    size="small"
                                    onClick={() => setViewMode('grid')}
                                    sx={{
                                        bgcolor: viewMode === 'grid' ? '#10B981' : 'transparent',
                                        color: viewMode === 'grid' ? '#FFF' : '#555',
                                        borderRadius: '6px'
                                    }}
                                >
                                    <GridViewIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    onClick={() => setViewMode('list')}
                                    sx={{
                                        bgcolor: viewMode === 'list' ? '#10B981' : 'transparent',
                                        color: viewMode === 'list' ? '#FFF' : '#555',
                                        borderRadius: '6px'
                                    }}
                                >
                                    <ViewListIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>
                    </Box>

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}>
                            <CircularProgress sx={{ color: '#10B981' }} />
                        </Box>
                    ) : (
                        <>
                            {viewMode === 'grid' ? (
                                <Grid container spacing={3} sx={{ mb: 6 }}>
                                    {paginatedDeployments.map((deployment) => (
                                        <Grid item xs={12} sm={6} md={4} key={deployment.deployment_id}>
                                            <DeploymentCard
                                                deployment={deployment}
                                                onClick={(id: string) => navigate(`/admin/deployment/${id}/upload`)}
                                                onEdit={() => { setSelectedDeployment(deployment); setIsEditDialogOpen(true); }}
                                                onDelete={(dep: DeploymentAdminListDetail) => { setSelectedDeployment(dep); setOpenConfirmDialog(true); }}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            ) : (
                                <Box sx={{ mb: 6 }}>
                                    <Box sx={{
                                        display: 'grid',
                                        gridTemplateColumns: '2fr 1.5fr 1.2fr 1.2fr 1fr 1fr 120px 100px 100px',
                                        px: 2,
                                        py: 1.5,
                                        borderBottom: '1px solid #222'
                                    }}>
                                        {['NAME', 'PROJECT', 'SITE', 'DEVICE', 'START', 'END', 'STATUS', 'FILES', 'ACTION'].map((h, i) => (
                                            <Typography key={h} variant="caption" sx={{ color: i === 0 ? '#EAB308' : '#666', fontWeight: 700 }}>{h}</Typography>
                                        ))}
                                    </Box>
                                    {paginatedDeployments.map((deployment) => (
                                        <DeploymentRow
                                            key={deployment.deployment_id}
                                            deployment={deployment}
                                            onClick={(id: string) => navigate(`/admin/deployment/${id}/upload`)}
                                            onEdit={() => { setSelectedDeployment(deployment); setIsEditDialogOpen(true); }}
                                            onDelete={(dep: DeploymentAdminListDetail) => { setSelectedDeployment(dep); setOpenConfirmDialog(true); }}
                                        />
                                    ))}
                                </Box>
                            )}

                            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
                                <Pagination
                                    count={Math.ceil(filteredDeployments.length / rowsPerPage)}
                                    page={page}
                                    onChange={(_, v) => setPage(v)}
                                    sx={{
                                        '& .MuiPaginationItem-root': { color: '#666' },
                                        '& .Mui-selected': { bgcolor: '#10B981 !important', color: '#FFF' },
                                        '& .MuiPaginationItem-ellipsis': { color: '#333' }
                                    }}
                                />
                            </Box>
                        </>
                    )}
                </Grid>

                <Grid item xs={12} lg={3}>
                    <Card sx={{ bgcolor: '#111', border: '1px solid #222', borderRadius: '16px', height: '100%' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ color: '#FFF', fontWeight: 700, mb: 4 }}>
                                RECENT ACTIVITY
                            </Typography>
                            <Box>
                                {recentActivities.map((activity, index) => (
                                    <ActivityItem key={index} activity={{ ...activity, isLast: index === recentActivities.length - 1 }} />
                                ))}
                                {recentActivities.length === 0 && (
                                    <Typography variant="body2" sx={{ color: '#444', fontStyle: 'italic' }}>
                                        No recent activity recorded.
                                    </Typography>
                                )}
                            </Box>
                            <Button
                                fullWidth
                                sx={{ color: '#10B981', mt: 2, textTransform: 'none', fontWeight: 600 }}
                            >
                                View all activities
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <ConfirmationDialog
                open={openConfirmDialog}
                onCancel={() => setOpenConfirmDialog(false)}
                onConfirm={handleDeleteDeployment}
                title="Delete Deployment"
                content="Are you sure you want to delete this deployment? This action will also delete all associated files and inferences."
            />

            <DeploymentDialog
                open={isCreateDialogOpen || isEditDialogOpen}
                onClose={() => { setIsCreateDialogOpen(false); setIsEditDialogOpen(false); }}
                deployment={isEditDialogOpen ? selectedDeployment : null}
                onSuccess={(dep) => {
                    if (isEditDialogOpen) {
                        dispatch({
                            type: 'ADMIN_SET_DEPLOYMENT_LIST',
                            payload: deployments.map(d => d.deployment_id === dep.deployment_id ? dep : d)
                        });
                    } else {
                        dispatch({ type: 'ADMIN_SET_DEPLOYMENT_LIST', payload: [dep, ...deployments] });
                    }
                }}
            />
        </Box>
    );
};

export default Deployments;